<template>
    <div>
      <StackedHorizontalBarChart
        :title=chartTitle
        :filterContext="filterContext"
        :dataProviders="dataProviders"
        :contextUpdaters="contextUpdaters"
        :navigationConfig="navigationConfig"
        :chartFontColor="chartFontColor"
        @updateFilter="handleFilterUpdate"
        :currentLevel="currentLevel"
      />
    </div>
  </template>
  
<script setup>
  import { computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n';
  import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';

  
  const store = useStore();
  const { t } = useI18n();
  const filterContext = computed(() => store.state.filterItemsContext);
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const conn = store.getters.getDBConnection;

  const chartName = 'categoryStackedBarChart';
  const chartTitle = t('itemBreakdown');

  const currentLevel = ref(0);
  const selectedType = ref(null);
  

const getCategoryData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
        c.id AS id,
        c.title AS title,
        s.title as status_title,
        s.color as color,
        SUM(COUNT(*)) OVER (PARTITION BY c.id, c.title, s.title, s.color) AS value,
        MIN(s.sort) as sort
    FROM ${table} i
    JOIN item_templates it ON i.profileId = it.id
    JOIN categories c ON it.categoryId = c.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY c.id, c.title, s.title, s.color
    ORDER BY c.title, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};

const getSubcategoryData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      it.subcategoryId AS id,
      it.type as parentId,
      it.subcategoryTitle AS title,
      s.title AS status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY it.subcategoryId, it.type, it.subcategoryTitle, s.color, s.title, s.sort) AS value,
      MIN(s.sort) as sort
    FROM ${table} i
    JOIN item_templates it ON i.profileId = it.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY it.subcategoryId, it.type, it.subcategoryTitle, s.color, s.title, s.sort
    ORDER BY it.subcategoryTitle, s.sort, s.title ASC
  `;
  
  return executeAndProcessQuery(query);
};
   
const getProfileData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      it.id AS id,
      it.code AS parentId,
      i.type AS type,
      it.title AS title,
      s.title AS status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY it.id, it.code, i.type, it.title, s.color, s.sort) AS value,
      MIN(s.sort) as sort
    FROM ${table} i
    JOIN item_templates it ON i.profileId = it.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY it.id, it.code, i.type, it.title, s.title, s.color, s.sort
    ORDER BY it.title, s.sort, s.title ASC
  `;
  
  return executeAndProcessQuery(query);
};

const getSubitemData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      ft.code AS id,
      ft.code AS parentId,
      i.type AS type,
      ft.title AS title,
      s.title AS status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY ft.code, i.type, ft.title, s.color, s.sort) AS value,
      MIN(s.sort) as sort
    FROM ${table} i
    JOIN form_templates ft ON i.SubItemTemplateId = ft.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY ft.code, i.type, ft.title, s.title, s.color, s.sort
    ORDER BY ft.title, s.sort, s.title ASC
  `;
  
  return executeAndProcessQuery(query);
};

const getStatusData = async (filtered = true) => {
    const table = filtered ? 'filteredItems' : 'items';
    const query = `
        SELECT 
            s.title as id,
            it.code AS parentId,
            i.type AS type,
            s.title AS title,
            s.title AS status_title,
            s.color as color,
            SUM(COUNT(*)) OVER (PARTITION BY s.title, it.code, i.type, s.title, s.color) AS value,
            MIN(s.sort) as sort
        FROM ${table} i
        JOIN status_templates s ON i.StatusId = s.id
        JOIN item_templates it ON i.profileId = it.id
        GROUP BY s.title, it.code, i.type, s.title, s.color
        HAVING COUNT(*) > 0
        ORDER BY s.title
    `
    return executeAndProcessQuery(query);
};

const getSingleStatusData = async (filtered = true) => {
    const table = filtered ? 'filteredItems' : 'items';
    const query = `
        SELECT 
            s.title as id,
            s.title AS title,
            s.title AS status_title,
            s.color as color,
            SUM(COUNT(*)) OVER (PARTITION BY s.id, s.title, s.color) AS value,
            MIN(s.sort) as sort
        FROM ${table} i
        JOIN status_templates s ON i.StatusId = s.id
        GROUP BY s.id, s.title, s.color
        HAVING COUNT(*) > 0
        ORDER BY s.title
    `
    return executeAndProcessQuery(query);
};

const getCategoryFilterContext = async (categoryId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN item_templates it ON i.profileId = it.id
    WHERE it.categoryId = '${categoryId}'
  `;
  await updateFilteredItemsContext(query);
};

const getSubcategoryFilterContext = async (subcategoryId, type) => {
  selectedType.value = type;
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN item_templates it ON i.profileId = it.id
    WHERE it.subcategoryID = '${subcategoryId}'
  `;
  await updateFilteredItemsContext(query);
};

const getProfileFilterContext = async (profileId) => {

   //short circuit to next level 
  if (selectedType.value === 'IssueTemplate') {
    currentLevel.value = 'status';
  }

  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    WHERE i.profileId = '${profileId}'
  `;
  await updateFilteredItemsContext(query);
};

const getSubitemFilterContext = async (code) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN form_templates ft ON i.SubItemTemplateId = ft.id
    WHERE ft.code = '${code}'
  `;
  await updateFilteredItemsContext(query);
};

const getSingleStatusFilterContext = async (statusTitle, code, type) => {
    const query = `
            SELECT DISTINCT i.SubItemId
            FROM items i
            JOIN status_templates st ON i.StatusId = st.id
            JOIN item_templates it ON i.profileId = it.id
            WHERE st.title = '${statusTitle}' AND it.code = '${code}' and i.type = '${type}'
        `;
    await updateFilteredItemsContext(query);
};

const resetFilterContext = async () => {
    selectedType.value = null;
    await filterContext.value.removeFilter(chartName);
}

  const dataProviders = {
    category: getCategoryData,
    subcategory: getSubcategoryData,
    profile: getProfileData,
    subitem: getSubitemData,
    status: getStatusData,
    singlestatus: getSingleStatusData
  };
  
  const contextUpdaters = {
    category: getCategoryFilterContext,
    subcategory: getSubcategoryFilterContext,
    profile: getProfileFilterContext,
    subitem: getSubitemFilterContext,
    status: getSingleStatusFilterContext,
    reset: resetFilterContext /*required*/
  };
  
  const navigationConfig = [
    { level: 'category', titleKey: 'category' },
    { level: 'subcategory', titleKey: 'subcategory' },
    { level: 'profile', titleKey: 'profile' },
    { level: 'subitem', titleKey: 'subitem'},
    { level: 'status', titleKey: 'status' },
    { level: 'singlestatus', titleKey: 'singlestatus'}
  ];


  const updateFilteredItemsContext = async (query) => {
    await filterContext.value.updateFilterWithQuery(chartName, query);
  };

  // Shared data processing utility function
const processChartData = (rows) => {
  const groupedData = {};
  
  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    const type = row['type'] ? row['type'] : null
    
    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        type: type,
        children: []
      };
    }
    
    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );
    
    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] = row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });
  
  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Generic query execution function
const executeAndProcessQuery = async (query, idField, titleField) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });
  
  return processChartData(rows, idField, titleField);
};

  </script>